// Best-effort helpers for downloading and copying the original image.

export async function downloadImage(url: string, filename: string): Promise<void> {
  try {
    const res = await fetch(url, { credentials: "same-origin" })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const blob = await res.blob()
    const objectUrl = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = objectUrl
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(objectUrl)
  } catch {
    window.open(url, "_blank", "noopener,noreferrer")
  }
}

/** Скачивание уже полученного Blob как файла (без fetch). */
export function downloadBlob(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = objectUrl
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(objectUrl)
}

/** Перегон в PNG: копирование в буфер требует image/png, вставка — им же чинит чужие форматы. */
export async function blobToPng(blob: Blob): Promise<Blob> {
  if (blob.type === "image/png") return blob
  const bitmap = await createImageBitmap(blob)
  try {
    const canvas = document.createElement("canvas")
    canvas.width = bitmap.width
    canvas.height = bitmap.height
    const ctx = canvas.getContext("2d")
    if (!ctx) throw new Error("Canvas 2D context unavailable")
    ctx.drawImage(bitmap, 0, 0)
    const png = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"))
    if (!png) throw new Error("PNG conversion failed")
    return png
  } finally {
    bitmap.close()
  }
}

export async function copyImageToClipboard(url: string): Promise<void> {
  if (!navigator.clipboard || typeof ClipboardItem === "undefined") {
    throw new Error("Clipboard API недоступен")
  }
  const res = await fetch(url, { credentials: "same-origin" })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  const png = await blobToPng(await res.blob())
  await navigator.clipboard.write([new ClipboardItem({ "image/png": png })])
}

/** Join slot label and entity name: "Лист героя - Мира". Omits dangling " - " when either side is empty. */
export function labeledDownloadBasename(label: string, entityName?: string | null): string {
  const a = label.trim()
  const b = entityName?.trim() ?? ""
  if (a && b) return `${a} - ${b}`
  return a || b
}

/** Build a download filename from a URL path, falling back to a default. */
export function filenameFromUrl(url: string, fallback: string, defaultExt = ".jpg"): string {
  try {
    const path = new URL(url, window.location.origin).pathname
    // Аудио тоже здесь: без него mp3 сохранялся бы как «Название.jpg».
    const ext = path.match(/\.(jpe?g|png|webp|gif|mp4|webm|mov|mp3|wav|m4a|ogg|aac|flac)$/i)?.[0] ?? defaultExt
    return `${fallback}${ext}`
  } catch {
    return `${fallback}${defaultExt}`
  }
}

export function openImageInNewTab(url: string | null | undefined): void {
  if (!url) return
  window.open(url, "_blank", "noopener,noreferrer")
}
